import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, FileText, X, Send, MessagesSquare } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useLang } from "@/context/Lang";
import { CONTACT } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function FloatingActions() {
  const { t } = useLang();
  const [chatOpen, setChatOpen] = useState(false);
  const [exit, setExit] = useState(false);
  const sessionId = useRef("s_" + Math.random().toString(36).slice(2));
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [phone, setPhone] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    setMsgs([{ from: "bot", text: t.chat.welcome }]);
  }, [t.chat.welcome]);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [msgs, chatOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem("exitShown")) {
        setExit(true);
        sessionStorage.setItem("exitShown", "1");
      }
    };
    document.addEventListener("mouseout", handler);
    return () => document.removeEventListener("mouseout", handler);
  }, []);

  const send = async () => {
    if (!input.trim()) return;
    const text = input;
    setMsgs((m) => [...m, { from: "user", text }]);
    setInput("");
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current, text, phone: phone || null,
      });
      setMsgs((m) => [...m, { from: "bot", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { from: "bot", text: "..." }]);
    }
  };

  return (
    <>
      {/* Desktop floating buttons */}
      <div className="hidden md:flex fixed right-5 bottom-5 z-40 flex-col gap-3">
        <a
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noreferrer"
          data-testid="float-whatsapp"
          className="w-14 h-14 rounded-full bg-[#25D366] hover:scale-110 text-white flex items-center justify-center shadow-lg transition-transform"
        >
          <MessageCircle size={26} />
        </a>
        <button
          onClick={() => setChatOpen((o) => !o)}
          data-testid="float-chat"
          className="w-14 h-14 rounded-full bg-orange hover:scale-110 text-white flex items-center justify-center shadow-lg transition-transform"
        >
          {chatOpen ? <X size={24} /> : <MessagesSquare size={24} />}
        </button>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-navy/90 backdrop-blur-xl border-t border-white/10 grid grid-cols-3">
        <a href={CONTACT.phoneHref} data-testid="mobile-call" className="flex flex-col items-center gap-0.5 py-3 text-white">
          <Phone size={20} /><span className="text-[11px] font-semibold">{t.mobile.call}</span>
        </a>
        <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp" className="flex flex-col items-center gap-0.5 py-3 text-[#25D366] border-x border-white/10">
          <MessageCircle size={20} /><span className="text-[11px] font-semibold">WhatsApp</span>
        </a>
        <button onClick={() => scrollTo("quote")} data-testid="mobile-quote" className="flex flex-col items-center gap-0.5 py-3 text-orange">
          <FileText size={20} /><span className="text-[11px] font-semibold">{t.mobile.quote}</span>
        </button>
      </div>

      {/* Chat widget */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            data-testid="chat-widget"
            className="fixed z-40 bottom-24 right-5 w-[90vw] max-w-sm bg-white border border-navy/15 shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-navy text-white px-4 py-3 flex items-center gap-2">
              <MessagesSquare size={18} className="text-orange" />
              <span className="font-bold">{t.chat.title}</span>
            </div>
            <div ref={scrollRef} className="flex-1 max-h-72 overflow-y-auto p-4 space-y-3 bg-[#F8F9FA]">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3.5 py-2.5 text-sm rounded-lg ${m.from === "user" ? "bg-orange text-white" : "bg-white border border-navy/10 text-navy"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-navy/10 bg-white space-y-2">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="chat-phone"
                placeholder={t.form.phone}
                className="w-full text-sm px-3 py-2 border border-navy/15 outline-none focus:border-orange"
              />
              <div className="flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="chat-input"
                  placeholder={t.chat.placeholder}
                  className="flex-1 text-sm px-3 py-2 border border-navy/15 outline-none focus:border-orange"
                />
                <button onClick={send} data-testid="chat-send" className="bg-orange text-white px-3.5 flex items-center justify-center">
                  <Send size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Exit intent popup */}
      <Dialog open={exit} onOpenChange={setExit}>
        <DialogContent className="max-w-md rounded-none border-0 p-0 overflow-hidden" data-testid="exit-popup">
          <div className="bg-navy grain relative p-8 text-center">
            <h3 className="font-display font-black text-white text-3xl md:text-4xl tracking-tighter">{t.exit.title}</h3>
            <p className="mt-4 text-white/70">{t.exit.sub}</p>
            <button
              onClick={() => { setExit(false); scrollTo("quote"); }}
              data-testid="exit-cta"
              className="mt-6 w-full bg-orange hover:bg-[#1550C0] text-white font-bold py-3.5 rounded-full transition-colors"
            >
              {t.exit.cta}
            </button>
            <button onClick={() => setExit(false)} data-testid="exit-dismiss" className="mt-3 text-white/40 text-sm hover:text-white/70 transition-colors">
              {t.exit.dismiss}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
